/**
 * Tenant Registration Form Component
 * Form for registering existing O365 tenants with validation.
 * Includes domain validation and access verification.
 * Note: Azure Tenant ID is auto-discovered by the backend from the domain.
 */

import React, { useState } from 'react';
import {
  Button,
  Field,
  Input,
  Textarea,
  Spinner,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { TenantApiService } from '../../services/tenantApi';
import { TenantRequestApiService } from '../../services/tenantRequestApi';
import { config } from '../../config';
import { Alert } from '../common/Alert';
import { AdminConsentModal, AdminConsentApp } from './AdminConsentModal';
import type { TenantRegistrationRequest } from '../../types/tenant';

/**
 * Form mode determines which fields are shown and submission behavior
 * - 'register': Form with name, domain, notes (tenant_id auto-discovered from domain)
 * - 'request': Simplified form with name and notes only (for org owners)
 */
type FormMode = 'register' | 'request';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalL),
    maxWidth: '600px',
  },
  buttonGroup: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalM),
    marginTop: tokens.spacingVerticalM,
  },
  syncingDialog: {
    minWidth: '400px',
    maxWidth: '400px',
  },
  syncingOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 1,
    pointerEvents: 'all' as const,
  },
  formContainer: {
    position: 'relative' as const,
  },
});

/**
 * Missing app info for consent flow (returned from backend 403 response)
 */
interface MissingApp {
  client_id: string;
  name: string;
}

/**
 * Prefill data for fulfill flow (when approving a tenant request)
 */
interface PrefillData {
  tenant_name?: string;
  notes?: string;
}

/**
 * Tenant Registration Form Props
 */
interface TenantRegistrationFormProps {
  /**
   * Form mode: 'register' for full form, 'request' for simplified name/notes only
   * @default 'register'
   */
  mode?: FormMode;
  /**
   * Organization ID - required when mode is 'request'
   */
  organizationId?: number;
  /**
   * Optional callback when registration completes successfully.
   * For register mode, passes the tenant database ID for linking to requests.
   */
  onSuccess?: (tenantDbId?: number) => void;
  /**
   * Optional callback when form is cancelled
   */
  onCancel?: () => void;
  /**
   * Optional initial data for edit mode or fulfillment pre-fill
   * Note: tenant_id is kept for edit mode (existing tenants have tenant_id)
   */
  initialData?: {
    tenant_id?: string;
    tenant_name: string;
    domain: string;
    notes: string;
    organization_id?: number;
  };
  /**
   * Enable edit mode - disables tenant_id and domain fields
   */
  editMode?: boolean;
  /**
   * Prefill data for fulfill flow (pre-fill name/notes from approved request)
   */
  prefillData?: PrefillData;
}

/**
 * Validate domain format
 * Note: Tenant ID is auto-discovered by backend from domain, so no GUID validation needed
 */
const validateDomain = (domain: string): string | null => {
  if (!domain) {
    return 'Domain is required';
  }
  if (domain.length < 3) {
    return 'Domain must be at least 3 characters';
  }
  if (!domain.includes('.')) {
    return 'Domain must contain at least one dot';
  }
  return null;
};

/**
 * Tenant Registration Form Component
 * Provides form for registering existing O365 tenants.
 * Includes validation and access verification capabilities.
 *
 * @example
 * ```tsx
 * <TenantRegistrationForm
 *   onSuccess={() => console.log('Tenant registered!')}
 *   onCancel={() => console.log('Registration cancelled')}
 * />
 * ```
 */
export const TenantRegistrationForm: React.FC<TenantRegistrationFormProps> = ({
  mode = 'register',
  organizationId: _organizationId,
  onSuccess,
  onCancel,
  initialData,
  editMode = false,
  prefillData,
}) => {
  const styles = useStyles();

  // Determine initial values: initialData (edit mode) > prefillData (fulfill flow) > empty
  const [formData, setFormData] = useState<TenantRegistrationRequest>({
    tenant_id: initialData?.tenant_id || '',
    tenant_name: initialData?.tenant_name || prefillData?.tenant_name || '',
    domain: initialData?.domain || '',
    notes: initialData?.notes || prefillData?.notes || '',
    organization_id: initialData?.organization_id,
  });

  // Form mode determines which fields are shown
  const isRequestMode = mode === 'request';

  // Note: tenant_id validation removed - auto-discovered by backend from domain
  const [validationErrors, setValidationErrors] = useState<{
    tenant_name?: string;
    domain?: string;
    notes?: string;
  }>({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [missingApps, setMissingApps] = useState<MissingApp[]>([]);
  // Discovered tenant_id from backend (for consent URL and retry)
  const [discoveredTenantId, setDiscoveredTenantId] = useState<string>('');
  const [showSyncingModal, setShowSyncingModal] = useState(false);
  const [syncingStatus, setSyncingStatus] = useState<string>('');

  /**
   * Handle input change and clear validation errors
   */
  const handleInputChange = (field: keyof TenantRegistrationRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    setSubmitError(null);
  };

  /**
   * Validate form data based on mode
   * - register mode: validates tenant_name and domain (tenant_id auto-discovered from domain)
   * - request mode: validates tenant_name and notes (notes required for requests)
   */
  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    // Only validate domain in register mode (tenant_id auto-discovered from domain)
    if (!isRequestMode) {
      const domainError = validateDomain(formData.domain);
      if (domainError) {
        errors.domain = domainError;
      }
    }

    // Tenant name is always required
    if (!formData.tenant_name.trim()) {
      errors.tenant_name = 'Tenant name is required';
    }

    // Notes are required in request mode
    if (isRequestMode && !formData.notes.trim()) {
      errors.notes = 'Notes are required for tenant requests';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle form submission
   * In edit mode: Updates tenant metadata
   * In request mode: Creates a tenant request (org owner flow)
   * In register mode: Registers tenant (backend discovers tenant_id from domain)
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      if (editMode) {
        // Edit mode - update tenant metadata (uses existing tenant_id from initialData)
        if (!formData.tenant_id) {
          throw new Error('tenant_id is required in edit mode');
        }
        // Always send notes value (even empty string) to allow clearing notes
        await TenantApiService.updateTenant(formData.tenant_id, {
          tenant_name: formData.tenant_name,
          notes: formData.notes,
        });

        // Call parent success handler
        if (onSuccess) {
          await onSuccess();
        }
      } else if (isRequestMode) {
        // Request mode - create tenant request (org owner flow)
        // Organization ID comes from the current user's auth context on the backend
        await TenantRequestApiService.createRequest({
          requested_name: formData.tenant_name,
          notes: formData.notes || undefined,
        });

        // Reset form
        setFormData({
          tenant_id: '',
          tenant_name: '',
          domain: '',
          notes: '',
          organization_id: undefined,
        });

        // Call parent success handler
        if (onSuccess) {
          await onSuccess();
        }
      } else {
        // Register mode - backend discovers tenant_id from domain and registers tenant
        setIsSubmitting(false);
        setShowSyncingModal(true);
        setSyncingStatus('Discovering tenant and registering...');

        const registeredTenant = await TenantApiService.registerTenant(formData);

        setSyncingStatus('Completing setup...');

        // Reset form
        setFormData({
          tenant_id: '',
          tenant_name: '',
          domain: '',
          notes: '',
          organization_id: undefined,
        });

        // Close syncing modal
        setShowSyncingModal(false);
        setSyncingStatus('');

        // Call parent success handler with tenant database ID (handles refreshing tenant list)
        if (onSuccess) {
          await onSuccess(registeredTenant.id);
        }
      }
    } catch (err) {
      // Handle registration errors - check for consent required (403 with tenant_id)
      setShowSyncingModal(false);
      setSyncingStatus('');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorResponse = (err as any)?.response;
      if (errorResponse?.status === 403 && errorResponse?.data?.tenant_id) {
        // Backend discovered tenant_id but consent is needed
        const tenantId = errorResponse.data.tenant_id;
        setDiscoveredTenantId(tenantId);

        // Check for missing_apps array (multiple apps need consent)
        const missingAppsFromResponse = errorResponse.data.missing_apps;
        if (Array.isArray(missingAppsFromResponse) && missingAppsFromResponse.length > 0) {
          setMissingApps(missingAppsFromResponse);
        } else {
          // Fallback for single app (backward compatibility)
          setMissingApps([{ client_id: config.azureClientId, name: 'IRPilot Service App' }]);
        }

        setShowConsentDialog(true);
        setIsSubmitting(false);
        return;
      }

      // Handle other errors
      let errorMessage: string;
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (editMode) {
        errorMessage = 'Failed to update tenant';
      } else if (isRequestMode) {
        errorMessage = 'Failed to submit tenant request';
      } else {
        errorMessage = 'Failed to register tenant';
      }
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    setFormData({
      tenant_id: '',
      tenant_name: '',
      domain: '',
      notes: '',
      organization_id: undefined,
    });
    setValidationErrors({});
    setSubmitError(null);
    onCancel?.();
  };

  /**
   * Handle successful admin consent verification from AdminConsentModal.
   * AdminConsentModal has already verified access, so we proceed directly with registration.
   */
  const handleConsentVerifySuccess = async () => {
    // Close consent dialog and show syncing modal
    setShowConsentDialog(false);
    setShowSyncingModal(true);
    setSyncingStatus('Registering tenant and syncing users...');

    try {
      // Register the tenant - this may take a while for large tenants as it syncs all users
      const registeredTenant = await TenantApiService.registerTenant(formData);

      setSyncingStatus('Completing setup...');

      // Reset state
      setDiscoveredTenantId('');
      setMissingApps([]);
      setFormData({
        tenant_id: '',
        tenant_name: '',
        domain: '',
        notes: '',
        organization_id: undefined,
      });

      // Close syncing modal
      setShowSyncingModal(false);
      setSyncingStatus('');

      // Call parent success handler with tenant database ID (handles refreshing tenant list)
      if (onSuccess) {
        await onSuccess(registeredTenant.id);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to register tenant';
      setSubmitError(errorMessage);
      setShowSyncingModal(false);
      setSyncingStatus('');
      // Re-show consent dialog if registration fails
      setShowConsentDialog(true);
    }
  };

  /**
   * Close consent dialog
   */
  const handleCloseConsentDialog = () => {
    setShowConsentDialog(false);
    setMissingApps([]);
    setDiscoveredTenantId('');
  };

  /**
   * Get description for an app based on its name
   */
  const getAppDescription = (appName: string): string => {
    if (appName.toLowerCase().includes('service')) {
      return 'Grants permission for IRPilot to manage users, licenses, and security policies in this tenant.';
    }
    if (appName.toLowerCase().includes('auth')) {
      return 'Enables users in this tenant to sign in to IRPilot using their Entra ID account.';
    }
    return 'Required for IRPilot to operate in this tenant.';
  };

  return (
    <>
      <div className={styles.formContainer}>
        {/* Overlay when syncing is in progress */}
        {showSyncingModal && <div className={styles.syncingOverlay} />}

        <form className={styles.form} onSubmit={handleSubmit} data-testid="tenant-registration-form">
          {/* Success Message */}
          {submitSuccess && (
            <Alert intent="success">
              Tenant registered successfully!
            </Alert>
          )}

          {/* Error Message */}
          {submitError && (
            <Alert intent="error" data-testid="error-message">
              {submitError}
            </Alert>
          )}

      {/* Note: Tenant ID field removed - auto-discovered by backend from domain */}

      {/* Tenant Name */}
      <Field
        label="Tenant Name"
        required
        validationMessage={validationErrors.tenant_name}
        validationState={validationErrors.tenant_name ? 'error' : 'none'}
      >
        <Input
          value={formData.tenant_name}
          onChange={(e) => handleInputChange('tenant_name', e.target.value)}
          placeholder="Acme Corporation"
          data-testid="tenant-name-input"
        />
      </Field>

      {/* Domain - only shown in register mode */}
      {!isRequestMode && (
        <Field
          label="Primary Domain"
          required
          validationMessage={validationErrors.domain}
          validationState={validationErrors.domain ? 'error' : 'none'}
        >
          <Input
            value={formData.domain}
            onChange={(e) => handleInputChange('domain', e.target.value)}
            placeholder="acme.onmicrosoft.com"
            data-testid="domain-input"
            disabled={editMode}
          />
        </Field>
      )}

      {/* Notes - required in request mode */}
      <Field
        label={isRequestMode ? 'Notes' : 'Notes (optional)'}
        required={isRequestMode}
        validationMessage={validationErrors.notes}
        validationState={validationErrors.notes ? 'error' : 'none'}
      >
        <Textarea
          value={formData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          placeholder={isRequestMode ? 'Provide a justification for this tenant request...' : 'Admin notes about this tenant...'}
          rows={3}
          data-testid="notes-input"
        />
      </Field>

      {/* Button Group */}
      <div className={styles.buttonGroup}>
        <Button
          type="submit"
          appearance="primary"
          disabled={isSubmitting || submitSuccess}
          icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
          data-testid="submit-button"
        >
          {isSubmitting
            ? (editMode ? 'Saving...' : isRequestMode ? 'Submitting...' : 'Registering...')
            : (editMode ? 'Save Changes' : isRequestMode ? 'Request Tenant' : 'Register Tenant')}
        </Button>

        {onCancel && (
          <Button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
        )}
        </div>
      </form>
      </div>

    {/* Admin Consent Modal */}
    {showConsentDialog && missingApps.length > 0 && (
      <AdminConsentModal
        isOpen={showConsentDialog}
        tenantId={discoveredTenantId}
        tenantName={formData.tenant_name || discoveredTenantId}
        apps={missingApps.map((app): AdminConsentApp => ({
          clientId: app.client_id,
          name: app.name,
          description: getAppDescription(app.name),
        }))}
        onVerifySuccess={handleConsentVerifySuccess}
        onClose={handleCloseConsentDialog}
        title="Admin Consent Required"
        verifyButtonText="Complete Registration"
        verifyingButtonText="Completing..."
      />
    )}

    {/* Syncing Modal - Non-dismissable during user sync */}
    <Dialog open={showSyncingModal}>
      <DialogSurface data-testid="syncing-modal" className={styles.syncingDialog}>
        <DialogBody>
          <DialogTitle>Registering Tenant</DialogTitle>
          <DialogContent>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: tokens.spacingVerticalL,
              padding: tokens.spacingVerticalXL,
            }}>
              <Spinner size="large" />
              <p style={{
                textAlign: 'center',
                color: tokens.colorNeutralForeground1,
                fontSize: tokens.fontSizeBase400,
                margin: 0,
              }}>
                {syncingStatus}
              </p>
              <p style={{
                textAlign: 'center',
                color: tokens.colorNeutralForeground3,
                fontSize: tokens.fontSizeBase200,
                margin: 0,
              }}>
                This may take a few minutes for large tenants.
              </p>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    </>
  );
};
